/**
 * Operator-facing labels for irrigation decisions.
 * Parses agent-authored reason strings for display only — never mutate Firestore
 * reasons (controller sizing/intent parsers depend on the raw text).
 */
import type {
  DecisionSizingSource,
  IrrigationDecision,
} from '../../shared/types/IrrigationRealTime';
import { formatDuration } from './formatDuration';
import { formatExecuteCondition } from './steeringFormat';

export const SIZING_SOURCE_LABEL: Record<DecisionSizingSource, string> = {
  stacking_plan: 'stacking plan (block physics)',
  stack_suggestion: 'stack sizing',
  leach_suggestion: 'leach sizing',
  autosearch_plan: 'AutoSearch plan',
  shot_planner: 'shot planner',
  p1_plan: 'P1 plan',
  schedule_fallback: 'schedule',
  manual: 'manual',
  controller_coverage_fallback: 'controller coverage',
  other: 'other',
};

const EC_STACK_LABEL: Record<string, string> = {
  p2_low_runoff: 'P2 no-runoff',
  overnight: 'overnight',
  both: 'overnight + P2',
};

/** Parentheticals that look like debug bags: (key=value; …) */
const DEBUG_PAREN_RE = /\s*\([^()]*=[^()]*\)/g;

/** Authorship tags the Mac/controller stamp into reason strings (metrics parse these). */
const AUTHORSHIP_TAG_RE = /\s*\[(?:silence_fallback|template|llm|coverage_fallback|mac_campaign)\]/gi;

/** Decision ids minted by the Mac / controller. Never render these in operator copy. */
export const INTERNAL_DECISION_ID_RE =
  /\b(?:llmparse|dec|irr|ctrl_coverage|mac_campaign)_[A-Za-z0-9_]+/g;

/** Strip machine decision ids. IDs may stay in tooltips (`title=`) only. */
export function scrubInternalIds(text: string): string {
  return text
    .replace(INTERNAL_DECISION_ID_RE, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([—–,;:.])/g, '$1')
    .trim();
}

export function sizingSourceLabel(source: string | null | undefined): string | null {
  if (!source) return null;
  if (source in SIZING_SOURCE_LABEL) {
    return SIZING_SOURCE_LABEL[source as DecisionSizingSource];
  }
  return source.replace(/_/g, ' ');
}

export function formatAuthorKind(
  d: Pick<IrrigationDecision, 'id' | 'createdBy'> & { agentId?: string; agentModel?: string },
): string | null {
  const id = String(d.id || '');
  // Per-decision crew agent attribution (ported from the retired DecisionQueuePanel).
  const agent = d.agentId
    ? ` [${d.agentId}${d.agentModel ? ` · ${d.agentModel}` : ''}]`
    : '';
  if (d.createdBy === 'fallback_scheduler' || id.startsWith('ctrl_coverage_')) {
    return 'Safety net';
  }
  // mac_campaign = the Mac deterministic layer authoring campaign pulses
  // (2026-08-30, day-shape-v3); same operator-facing concept as ec_campaign.
  if (d.createdBy === 'ec_campaign' || d.createdBy === 'mac_campaign') return 'EC campaign';
  if (id.includes('_p1ramp')) return `P1 ramp${agent}`;
  if (id.includes('_p2stand_r') || id.includes('_resize')) return `Resized${agent}`;
  if (id.includes('_p2stand')) return `Hold${agent}`;
  if (d.createdBy === 'irrigation_ai' || d.createdBy === 'growmaster_ai') return `AI${agent}`;
  if (d.createdBy) return `${d.createdBy.replace(/_/g, ' ')}${agent}`;
  return null;
}

export function formatGradeLabel(grade: string): string {
  if (!grade) return grade;
  return grade.charAt(0).toUpperCase() + grade.slice(1);
}

export function formatStatusLabel(status: string): string {
  switch (status) {
    case 'pending': return 'waiting';
    case 'executing': return 'running';
    case 'executed': return 'done';
    case 'rejected': return 'skipped';
    case 'expired': return 'expired';
    default: return status.replace(/_/g, ' ');
  }
}

/** Humanize controller outcome notes that still contain machine source keys. */
export function formatOutcomeNote(note: string): string {
  return scrubInternalIds(note.replace(
    /\b(stack_suggestion|autosearch_plan|shot_planner|p1_plan|schedule_fallback|live_suggestion)\b/g,
    (m) => sizingSourceLabel(m) ?? m,
  ));
}

/**
 * Invariant-monitor machine keys → operator phrases. Mirrors
 * controller/src/irrigationAlertCopy.ts INVARIANT_KEY_PHRASES: the controller
 * humanizes new alerts at source, but rows persisted before that fix (and any
 * future regression) reach the banner/inbox raw, so the display layer stays
 * the last line of defense per the human-readable-ui rule.
 */
const INVARIANT_KEY_PHRASES: Record<string, string> = {
  // Legacy key (pre-2026-08-28 day-shape rewrite) kept for persisted rows.
  stack_mode_not_concentration: 'EC stacking is not withholding water',
  stack_mode_window_mismatch: 'EC stacking is out of step with its daily rhythm',
  stack_shot_inside_cutoff: 'EC stacking watered after the end-of-day cutoff',
  dilute_active_below_target: 'flush still running below the EC target',
  dilute_latch_stuck_below_target: 'flush hold stuck below the EC target',
  runoff_duty_not_suspended_during_campaign: 'runoff correction active during an EC adjustment',
  campaign_churn: 'EC adjustment keeps starting and stopping',
  campaign_pulse_lights_off: 'EC adjustment watered after lights-off',
};

/**
 * Humanize an operator-facing alert title or message before render.
 * Applied once at notification ingest (NotificationContext) so the banner,
 * inbox, and toasts all inherit it.
 */
export function formatOperatorAlertText(text: string): string {
  if (!text) return text;
  let s = text.replace(
    /Irrigation invariant violated:\s*([a-z0-9_]+)\s*(—|-)?\s*/gi,
    (_m, key: string) => {
      const phrase = INVARIANT_KEY_PHRASES[key] ?? key.replace(/_/g, ' ');
      return `Irrigation self-check warning: ${phrase} — `;
    },
  );
  for (const [key, phrase] of Object.entries(INVARIANT_KEY_PHRASES)) {
    s = s.split(key).join(phrase);
  }
  s = s.replace(/published ecStackMode is '([a-z_]+)'/gi, (_m, mode: string) => {
    if (mode === 'off') return 'the watering plan has stopped the daytime feeding shots';
    if (mode === 'concentration') return 'the watering plan is withholding the daytime feeding shots';
    return 'the watering plan is still refilling the blocks';
  });
  s = s.replace(/\s*\(expected '([a-z_]+)'\)/gi, (_m, mode: string) =>
    (mode === 'irrigation'
      ? ' when it should be feeding small daytime shots'
      : ' when it should have stopped for the day'));
  s = s.replace(/\becStackMode\b/g, 'watering mode');
  return scrubInternalIds(s).replace(/\s*—\s*$/, '');
}

export type RejectionDisplayKind = 'replaced' | 'superseded' | 'operator' | 'other';

/** Humanize controller rejection reasons for the operator. IDs stay in `replacedId` only. */
export function formatRejectionDisplay(reason: string | null | undefined): {
  text: string;
  replacedId: string | null;
  kind: RejectionDisplayKind;
} {
  if (!reason) return { text: '', replacedId: null, kind: 'other' };

  const replaced = reason.match(/^Replaced by\s+(\S+)/i);
  if (replaced) {
    return { text: 'Replaced by a newer size', replacedId: replaced[1], kind: 'replaced' };
  }

  const superseded = reason.match(/^Superseded by\s+(\S+)/i);
  if (superseded) {
    return {
      text: 'Skipped — another shot for this program already ran',
      replacedId: superseded[1],
      kind: 'superseded',
    };
  }

  const replaceTarget = reason.match(/^Replace target\s+(\S+)\s+is executing/i);
  if (replaceTarget) {
    return {
      text: 'Skipped — the shot it replaces was already running',
      replacedId: replaceTarget[1],
      kind: 'replaced',
    };
  }

  if (/^Cross-intent replace rejected/i.test(reason)) {
    return {
      text: 'Skipped — this decision would replace the wrong type of shot',
      replacedId: null,
      kind: 'other',
    };
  }

  if (/^Manually rejected by user/i.test(reason)) {
    return { text: 'Skipped by operator', replacedId: null, kind: 'operator' };
  }

  if (/^pump_busy:/i.test(reason)) {
    return {
      text: scrubInternalIds(reason.replace(/^pump_busy:\s*/i, '')),
      replacedId: null,
      kind: 'other',
    };
  }

  if (/^duration_not_integer:/i.test(reason)) {
    return {
      text: 'Shot duration must be a whole number of seconds',
      replacedId: null,
      kind: 'other',
    };
  }

  if (/^hard_max:/i.test(reason)) {
    return {
      text: scrubInternalIds(reason.replace(/^hard_max:\s*/i, '')),
      replacedId: null,
      kind: 'other',
    };
  }

  return { text: scrubInternalIds(reason), replacedId: null, kind: 'other' };
}

export interface DecisionDisplay {
  title: string;
  detail?: string;
  rawReason: string;
}

function parseResizeSeconds(reason: string): { from: number; to: number } | null {
  const m = reason.match(/(\d+(?:\.\d+)?)\s*s\s*(?:→|->)\s*(\d+(?:\.\d+)?)\s*s/i);
  if (!m) return null;
  const from = Number(m[1]);
  const to = Number(m[2]);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return { from, to };
}

function parseSizingFromReason(reason: string): { source: string | null; seconds: number | null } {
  const m = reason.match(/sizing=([a-z0-9_]+)(?::(\d+(?:\.\d+)?)s?)?/i)
    ?? reason.match(/source=([a-z0-9_]+)/i);
  if (!m) return { source: null, seconds: null };
  return {
    source: m[1] ?? null,
    seconds: m[2] != null && Number.isFinite(Number(m[2])) ? Number(m[2]) : null,
  };
}

function parseEcStack(reason: string): string | null {
  const m = reason.match(/ecStack=([a-z0-9_]+)/i);
  if (!m) return null;
  return EC_STACK_LABEL[m[1]] ?? m[1].replace(/_/g, ' ');
}

function hasAuthorshipTag(reason: string, tag: string): boolean {
  return new RegExp(`\\[${tag}\\]`, 'i').test(reason);
}

function stripDebugBags(reason: string): string {
  return reason
    .replace(AUTHORSHIP_TAG_RE, '')
    .replace(DEBUG_PAREN_RE, '')
    .replace(/\s*[·;]\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function isCoverageFallback(
  d: Pick<IrrigationDecision, 'id' | 'reason' | 'createdBy' | 'evidence'>,
): boolean {
  if (d.createdBy === 'fallback_scheduler') return true;
  if (d.evidence?.sizingSource === 'controller_coverage_fallback') return true;
  if (String(d.id || '').startsWith('ctrl_coverage_')) return true;
  return /\[coverage_fallback\]/i.test(d.reason || '');
}

function isStandingReason(reason: string, id: string): boolean {
  const r = reason.toLowerCase();
  if (r.includes('standing trigger') || r.includes('standing dryback')) return true;
  if (id.includes('_p2stand') && !id.includes('_p2stand_r') && !id.includes('_resize')) return true;
  return false;
}

function isResizeReason(reason: string, id: string): boolean {
  if (/p2\s*re-?size/i.test(reason) || /re-?size:/i.test(reason)) return true;
  if (id.includes('_p2stand_r') || id.includes('_resize')) return true;
  return false;
}

function buildDetail(parts: Array<string | null | undefined>): string | undefined {
  const cleaned = parts.filter((p): p is string => !!p && p.trim().length > 0);
  return cleaned.length > 0 ? cleaned.join(' · ') : undefined;
}

function toDisplay(title: string, detail: string | undefined, rawReason: string): DecisionDisplay {
  return {
    title: scrubInternalIds(title),
    detail: detail ? scrubInternalIds(detail) : undefined,
    rawReason,
  };
}

/**
 * Build a short operator title + optional detail line from a decision.
 * Prefer structured fields; fall back to parsing common agent reason patterns.
 */
export function formatDecisionDisplay(
  d: Pick<
    IrrigationDecision,
    | 'id'
    | 'reason'
    | 'status'
    | 'durationSeconds'
    | 'executeCondition'
    | 'evidence'
    | 'createdBy'
  >,
): DecisionDisplay {
  const rawReason = (d.reason || '').trim();
  const id = String(d.id || '');
  const sizing = d.evidence?.sizingSource
    ? { source: d.evidence.sizingSource, seconds: d.evidence.sizingReferenceSeconds ?? null }
    : parseSizingFromReason(rawReason);
  const sizingLabel = sizingSourceLabel(sizing.source);
  const cond = formatExecuteCondition(d.executeCondition);
  const dur = Number.isFinite(d.durationSeconds) && d.durationSeconds > 0
    ? formatDuration(d.durationSeconds)
    : null;
  const pending = d.status === 'pending' || d.status === 'executing';
  const ecLabel = parseEcStack(rawReason);
  const silenceAutoPilot = hasAuthorshipTag(rawReason, 'silence_fallback')
    ? 'auto-pilot'
    : null;

  if (isCoverageFallback(d)) {
    const title = pending
      ? 'Safety top-up queued (AI quiet)'
      : 'Safety top-up (AI quiet)';
    const detail = buildDetail([
      dur,
      cond,
      sizingLabel ?? 'controller coverage',
    ]);
    return toDisplay(title, detail, rawReason);
  }

  if (isResizeReason(rawReason, id)) {
    const parsed = parseResizeSeconds(rawReason);
    const title = parsed
      ? `Resized shot ${formatDuration(parsed.from)} → ${formatDuration(parsed.to)}`
      : (dur ? `Resized shot → ${dur}` : 'Resized shot');
    const detail = buildDetail([
      sizingLabel,
      ecLabel,
      silenceAutoPilot,
      cond,
    ]);
    return toDisplay(title, detail, rawReason);
  }

  if (isStandingReason(rawReason, id)) {
    const title = pending ? 'P2 top-up queued' : 'P2 top-up';
    const detail = buildDetail([
      dur,
      cond,
      sizingLabel,
      ecLabel,
      silenceAutoPilot,
    ]);
    return toDisplay(title, detail, rawReason);
  }

  // Soft cleanup for other agent reasons
  let cleaned = stripDebugBags(rawReason);
  cleaned = cleaned
    .replace(/\bsizing=[a-z0-9_]+(?::\d+(?:\.\d+)?s?)?/gi, '')
    .replace(/\bsource=[a-z0-9_]+/gi, '')
    .replace(/\bdelta=-?\d+(?:\.\d+)?%/gi, '')
    .replace(/\becStack=[a-z0-9_]+/gi, '')
    .replace(/\bdrybackFromPeak=-?\d+(?:\.\d+)?/gi, '')
    .replace(/\s*[·;,]\s*[·;,]/g, ' · ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (cleaned) {
    // Light wording polish for common prefixes
    cleaned = cleaned
      .replace(/^P2 standing trigger:\s*/i, pending ? 'P2 top-up queued: ' : 'P2 top-up: ')
      .replace(/^P1 ramp[- ]?up\s*/i, 'P1 ramp ')
      .trim();
  }

  const title = cleaned
    || (id.includes('_p1ramp') ? (pending ? 'P1 ramp shot queued' : 'P1 ramp shot') : null)
    || (pending ? 'Pending AI decision' : 'AI decision');

  const detail = buildDetail([
    !cleaned && dur ? dur : null,
    cond,
    sizingLabel,
    ecLabel,
    silenceAutoPilot,
  ]);

  return toDisplay(title, detail, rawReason);
}

/** W4: requested (as authored) / committed (as stored) / executed (as run) — shown separately. */
export interface ExecutionTrace {
  requested: string;
  committed: string;
  executed: string;
  /** The stored order differs from the authored action (the controller refuses such an order). */
  mismatch: boolean;
}

function traceLabel(seconds: unknown, condition: unknown): string {
  const secs = typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? formatDuration(seconds) : '—';
  const cond = condition && typeof condition === 'object'
    ? formatExecuteCondition({ type: (condition as { type: string }).type, value: (condition as { value: number }).value } as never)
    : null;
  return cond ? `${secs} ${cond}` : secs;
}

/**
 * Build the requested / committed / executed line for an AI action order. Returns null for orders
 * without an authored action on record (Athena orders, orders committed before W4).
 */
export function formatExecutionTrace(
  d: Pick<IrrigationDecision, 'status' | 'durationSeconds' | 'executeCondition' | 'authoredAction' | 'actuation'>,
  shot?: { durationSeconds?: number } | null,
): ExecutionTrace | null {
  const a = d.authoredAction as Record<string, unknown> | undefined;
  if (!a) return null;
  const pulses = Array.isArray(a.pulses) ? (a.pulses as Array<{ durationSeconds?: number }>) : null;
  const requestedSeconds = pulses ? pulses.reduce((s, p) => s + (p.durationSeconds ?? 0), 0) : a.durationSeconds;
  const requested = traceLabel(requestedSeconds, a.trigger ?? null);
  const committed = traceLabel(d.durationSeconds, d.executeCondition ?? null);
  let executed: string;
  if (shot && typeof shot.durationSeconds === 'number') executed = formatDuration(shot.durationSeconds);
  else if (d.status === 'executed') executed = d.actuation ? 'valve acknowledged (duration not yet observed)' : 'executed (not observed)';
  else if (d.status === 'executing') executed = 'running';
  else if (d.status === 'pending') executed = '—';
  else executed = 'not executed';
  return { requested, committed, executed, mismatch: requested !== committed };
}

/**
 * W4: what an AI order (ai_realtime) is actually doing, from its own state — never a claim that it
 * waits on a gate the controller does not apply to AI orders (settle wait, minimum interval,
 * lights-off: removed on ai_realtime by the 2026-09-19 operator decision). A hold is shown with its
 * real reason; otherwise the order is running, armed on its authored trigger, scheduled for its
 * authored start, or due now.
 */
export function formatAiOrderStatus(
  d: Pick<IrrigationDecision, 'status' | 'hold' | 'executeCondition' | 'executeAfter'>,
  nowMs: number = Date.now(),
): { text: string; tone: 'muted' | 'warning' } {
  switch (d.hold) {
    case 'reauthorization_required':
      return { text: 'Held — queued under an earlier contract; the AI must adopt, replace or cancel it', tone: 'warning' };
    case 'rebind_required':
      return { text: 'Held — the valve binding changed since it was authorized; the AI must re-authorize it', tone: 'warning' };
    case 'transition_pending':
      return { text: 'Held — a declared transition is being applied', tone: 'warning' };
    case 'closure_unobserved':
      return { text: 'Waiting — the previous pulse’s valve closure is not yet confirmed', tone: 'warning' };
    default:
      if (d.hold) return { text: `Held (${String(d.hold).replace(/_/g, ' ')})`, tone: 'warning' };
  }
  if (d.status === 'executing') return { text: 'Running now', tone: 'muted' };
  const cond = formatExecuteCondition(d.executeCondition);
  if (cond) return { text: `Armed — fires ${cond}, exactly as authored`, tone: 'muted' };
  const start = d.executeAfter ? Date.parse(d.executeAfter) : NaN;
  if (Number.isFinite(start) && start > nowMs) {
    const hhmm = new Date(start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return { text: `Scheduled — starts ${hhmm}, as authored`, tone: 'muted' };
  }
  return { text: 'Due now — runs on the controller’s next pass', tone: 'muted' };
}
